import { Component, OnInit, OnDestroy } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { FunctionalLocationHierarchyService } from '../../services/functional-location-hierarchy.service';
import { PlantWithSectors } from '../../models/plant-with-sectors.model';
import { defaultOptions } from '../../shared/constants/sanitization-options.constans';
import { SanitizationFilterService } from '../../services/sanitization-filter.service';
import { IndexedDbService } from '../../services/indexed-db.service';

@Component({
  selector: 'app-sanitization',
  templateUrl: './sanitization.component.html',
  styleUrl: './sanitization.component.scss',
  standalone: false,
})
export class SanitizationComponent implements OnInit, OnDestroy {
  selectedOption = 'doublons';
  totalWorkNotificationRecords = 0;
  totalWorkOrderRecords = 0;
  plantOptions: SelectItem[] = defaultOptions;
  mainWorkCenterOptions: SelectItem[] = defaultOptions;
  private plantsWithSectors: PlantWithSectors[] = [];
  private destroy$ = new Subject<void>();
  private readonly plantsWithSectorsKey = 'plantsWithSectors';
  private readonly plantsWithSectorsHashKey = 'plantsWithSectorsHash';

  constructor(
    private functionalLocationHierarchyService: FunctionalLocationHierarchyService,
    private filterService: SanitizationFilterService,
    private indexedDbService: IndexedDbService,
  ) {}

  ngOnInit() {
    this.loadPlantsWithSectors();

    this.subscribePlantChanges();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadPlantsWithSectors() {
    this.functionalLocationHierarchyService
      .getPlantsWithMainWorkCentersHash()
      .subscribe({
        next: async (latestHash) => {
          const storedData = await this.getStoredPlantsWithSectors();
          const storedHash = await this.getStoredHash();

          if (storedData && storedHash && latestHash === storedHash) {
            this.applyPlantsWithMainWorkCenters(storedData);
          } else {
            this.fetchAndApplyPlantsWithMainWorkCenters();
          }
        },
        error: (err) =>
          console.error(
            'Error loading plants with main work centers hash:',
            err,
          ),
      });
  }

  private fetchAndApplyPlantsWithMainWorkCenters() {
    this.functionalLocationHierarchyService
      .getPlantsWithMainWorkCenters()
      .subscribe({
        next: async (data) => {
          this.applyPlantsWithMainWorkCenters(data.Plants);
          await this.indexedDbService.setItem(
            this.plantsWithSectorsKey,
            data.Plants,
          );
          await this.indexedDbService.setItem(
            this.plantsWithSectorsHashKey,
            data.CacheHash,
          );
        },
        error: (err) =>
          console.error('Error loading plants with main work centers:', err),
      });
  }

  private applyPlantsWithMainWorkCenters(data: PlantWithSectors[]) {
    this.plantsWithSectors = data;
    this.plantOptions = [
      ...defaultOptions,
      ...data.map((p) => ({ label: p.Plant, value: p.Plant })),
    ];
    const currentPlant = this.filterService.getShared().plant;
    this.updateMainWorkCenterOptions(currentPlant);
  }

  private async getStoredPlantsWithSectors(): Promise<
    PlantWithSectors[] | null
  > {
    try {
      const stored = await this.indexedDbService.getItem(
        this.plantsWithSectorsKey,
      );
      return stored ? (stored as PlantWithSectors[]) : null;
    } catch {
      return null;
    }
  }

  private async getStoredHash(): Promise<string | null> {
    try {
      const hash = await this.indexedDbService.getItem(
        this.plantsWithSectorsHashKey,
      );
      return hash as string | null;
    } catch {
      return null;
    }
  }

  private subscribePlantChanges() {
    this.filterService.plant$
      .pipe(takeUntil(this.destroy$))
      .subscribe((plant) => {
        this.updateMainWorkCenterOptions(plant);
        const currentMwcs = this.filterService.getShared().mainWorkCenters;
        if (currentMwcs?.length && this.plantsWithSectors.length > 0) {
          const validOptions = this.mainWorkCenterOptions.map((o) => o.value);
          const stillValid = currentMwcs.filter((mwc) =>
            validOptions.includes(mwc),
          );
          if (stillValid.length !== currentMwcs.length) {
            this.filterService.setMainWorkCenters(
              stillValid.length > 0 ? stillValid : null,
            );
          }
        }
      });
  }

  private updateMainWorkCenterOptions(plant: string | null) {
    if (!plant) {
      const allSectors = [
        ...new Set(this.plantsWithSectors.flatMap((p) => p.MainWorkCenters)),
      ].sort();
      this.mainWorkCenterOptions = allSectors.map((s) => ({
        label: s,
        value: s,
      }));
    } else {
      const found = this.plantsWithSectors.find((p) => p.Plant === plant);
      const sectors = found ? found.MainWorkCenters : [];
      this.mainWorkCenterOptions = sectors.map((s) => ({ label: s, value: s }));
    }
  }

  selectOption(option: string) {
    this.selectedOption = option;
  }
}
